/* =============================================================================
   Dateless market data model.

   Two assets, both static (delivery path is "regenerate asset", never "call
   an endpoint"):
   · assets/market-index/platex-index-series.json     (existing; 2015 = 100)
   · assets/market-index/dateless-format-medians.json (PlateX market-data
     standard v1.0; generated by platex-index emit_format_medians.py)

   The medians asset follows the reusable PlateX MARKET-DATA STANDARD:
   the same envelope (standard, dataset, generatedAt, methodologyVersion,
   pricingBasis, windows, confidenceScale, records) will carry Number Ones,
   Initials, Prefix, Suffix and Index datasets. Keep these interfaces
   dataset-agnostic where they describe the envelope.
   ========================================================================== */

export type MarketConfidence = 'High' | 'Medium' | 'Low' | 'Indicative';

/** One window's aggregate for a record (market-data standard). */
export interface MarketWindowAggregate {
  median: number;
  /** Observed min and max in the window (standard v1.1). */
  low?: number;
  high?: number;
  sampleSize: number;
  confidence: MarketConfidence;
}

/** Envelope shared by every PlateX market dataset (standard v1.0). */
export interface MarketDataEnvelope<TRecord> {
  standard: string;            // "platex-market-data/1.0"
  dataset: string;
  generatedAt: string;
  methodologyVersion: string;
  pricingBasis: string;
  pricingBasisNote?: string;
  source: {
    pipeline: string;
    inputFile?: string;
    corpusRows?: number;
    extractionSnapshot?: string;
    corpusCoverage?: string;
    frozen?: boolean;
  };
  windows: {
    presentation: string;
    definitions: { [name: string]: string };
  };
  confidenceScale: { [level: string]: string };
  notes?: string[];
  records: TRecord[];
}

/** The dateless-format-medians record shape. */
export interface FormatMedianRecord {
  classification: {
    letters: number;
    numbers: number;
    token: string;    // "2x1"
    display: string;  // "2×1"
  };
  /** Presentation-window (2018+) figures, the Dateless Reference standard. */
  median: number | null;
  /** Observed range across the presentation window. Where confidence is
      Indicative these are shown INSTEAD of the median: a median of a handful
      of sales is not a point estimate anyone should read as one. */
  windowLow?: number | null;
  windowHigh?: number | null;
  windowSampleSize: number;
  /** All-time count: a scarcity measure, NOT statistical support. */
  sampleSize: number;
  confidence: MarketConfidence;
  windows: { [window: string]: MarketWindowAggregate };
}

export type FormatMediansAsset = MarketDataEnvelope<FormatMedianRecord>;

export const FORMAT_MEDIANS_ASSET_URL =
  'assets/market-index/dateless-format-medians.json';
